/**
 * @page QmPipeline
 * @route /qm/pipeline
 * @description QM Modul 6c (#921c) — Pipeline-Dashboard mit 6 KPIs,
 *   Drill-Down-Listen und ISO-9001-Export-Anforderung.
 * @roles admin, administrator, ops_manager, cs_teamlead
 * @since v2.410.0
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import {
  Activity, AlertTriangle, BookOpen, MessageCircle, CheckCircle2, Clock,
  FileText, Download, RefreshCw, Loader2, X, ChevronRight, ShieldCheck, Filter
} from 'lucide-react';

const PIPE = '/api/qm/pipeline';
const ISO = '/api/qm/iso-9001-export';

function fmtInt(n) { return Number.isFinite(Number(n)) ? Number(n).toLocaleString('de-AT') : '—'; }
function fmtPct(n) { return n == null ? '—' : `${Math.round(Number(n))} %`; }
function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('de-AT');
}
function fmtDateTime(d) {
  if (!d) return '—';
  return new Date(d).toLocaleString('de-AT');
}
function fmtTageOffen(n) {
  if (n == null) return '—';
  const v = Number(n);
  if (!Number.isFinite(v)) return '—';
  return `${Math.floor(v)} d`;
}
function maName(o) { return [o?.nachname, o?.vorname].filter(Boolean).join(', ') || '—'; }

function api(path) {
  return fetch(path, { credentials: 'include' }).then(r => r.ok ? r.json() : Promise.reject(r));
}

function KpiCard({ label, value, sublabel, icon: Icon, accent = 'var(--text-primary)', onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`text-left bg-surface-card border border-line rounded-card p-4 transition-colors ${onClick ? 'hover:border-[#338edc] cursor-pointer' : ''}`}
      disabled={!onClick}
    >
      <div className="flex items-start justify-between mb-2">
        <div className="text-[11px] uppercase tracking-wider" style={{ color: 'var(--text-tertiary)' }}>{label}</div>
        {Icon && <Icon size={16} style={{ color: 'var(--text-tertiary)' }} />}
      </div>
      <div className="text-2xl font-bold" style={{ color: accent }}>{value}</div>
      {sublabel && <div className="text-[11px] mt-1" style={{ color: 'var(--text-tertiary)' }}>{sublabel}</div>}
    </button>
  );
}

function StatusPill({ status }) {
  const colors = {
    aktiv: { bg: '#10B98118', fg: '#10B981' },
    in_review: { bg: '#F59E0B18', fg: '#F59E0B' },
    entwurf: { bg: '#9CA3AF18', fg: '#6B7280' },
    archiviert: { bg: '#9CA3AF18', fg: '#9CA3AF' },
    offen: { bg: '#F59E0B18', fg: '#D97706' },
    tour_offen: { bg: '#338EDC18', fg: '#338EDC' },
    test_offen: { bg: '#6366F118', fg: '#6366F1' },
    bestanden: { bg: '#10B98118', fg: '#10B981' },
    nicht_bestanden: { bg: '#EF444418', fg: '#EF4444' },
    abgelaufen: { bg: '#9CA3AF18', fg: '#6B7280' },
  };
  const c = colors[status] || { bg: '#9CA3AF18', fg: '#6B7280' };
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold"
          style={{ backgroundColor: c.bg, color: c.fg }}>
      {status || '—'}
    </span>
  );
}

function PrioPill({ prio }) {
  const c = prio === 'hoch' ? { bg: '#EF444418', fg: '#EF4444' }
          : prio === 'mittel' ? { bg: '#F59E0B18', fg: '#D97706' }
          : { bg: '#10B98118', fg: '#10B981' };
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold"
          style={{ backgroundColor: c.bg, color: c.fg }}>
      {prio || '—'}
    </span>
  );
}

function DrillDownModal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-surface-card border border-line rounded-card max-w-5xl w-full max-h-[85vh] overflow-hidden flex flex-col"
           onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-line">
          <h3 className="text-base font-semibold" style={{ color: 'var(--text-primary)' }}>{title}</h3>
          <button onClick={onClose} className="text-[var(--text-tertiary)] hover:text-[var(--text-primary)]" aria-label="Close">
            <X size={20} />
          </button>
        </div>
        <div className="overflow-auto p-4">{children}</div>
      </div>
    </div>
  );
}

export default function QmPipeline() {
  const { t } = useLanguage();
  const [kpis, setKpis] = useState(null);
  const [prozesse, setProzesse] = useState([]);
  const [reviewsList, setReviewsList] = useState([]);
  const [schulungenList, setSchulungenList] = useState([]);
  const [feedbackList, setFeedbackList] = useState([]);
  const [zertifikateList, setZertifikateList] = useState([]);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterAbteilung, setFilterAbteilung] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [drill, setDrill] = useState(null); // 'aktive'|'review'|'schulungen'|'feedback'|'compliance'|'ablauf'

  // ISO Export
  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const yearStart = useMemo(() => `${new Date().getFullYear()}-01-01`, []);
  const [isoVon, setIsoVon] = useState(yearStart);
  const [isoBis, setIsoBis] = useState(today);
  const [isoJobs, setIsoJobs] = useState([]);
  const [isoStarting, setIsoStarting] = useState(false);
  const [activeJob, setActiveJob] = useState(null);

  const loadKpis = useCallback(async () => {
    try {
      const k = await api(`${PIPE}/kpis`);
      setKpis(k);
    } catch (e) { setError('KPI-Load: ' + (e.statusText || e.message || 'Fehler')); }
  }, []);

  const loadProzesse = useCallback(async () => {
    const qs = new URLSearchParams();
    if (filterStatus) qs.set('status', filterStatus);
    if (filterAbteilung) qs.set('abteilung_id', filterAbteilung);
    try {
      const r = await api(`${PIPE}/prozesse${qs.toString() ? '?' + qs : ''}`);
      setProzesse(r.prozesse || []);
    } catch (e) { /* noop */ }
  }, [filterStatus, filterAbteilung]);

  const loadLists = useCallback(async () => {
    try {
      const [r, s, f, z] = await Promise.all([
        api(`${PIPE}/reviews-faellig`),
        api(`${PIPE}/schulungs-backlog`),
        api(`${PIPE}/feedback-backlog`),
        api(`${PIPE}/zertifikate-ablauf?in_tagen=30`),
      ]);
      setReviewsList(r.reviews || []);
      setSchulungenList(s.backlog || []);
      setFeedbackList(f.feedback || []);
      setZertifikateList(z.zertifikate || []);
    } catch (e) { /* noop */ }
  }, []);

  const loadIsoJobs = useCallback(async () => {
    try {
      const r = await api(`${ISO}?limit=20`);
      setIsoJobs(r.jobs || []);
    } catch (e) { /* noop */ }
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    await Promise.all([loadKpis(), loadProzesse(), loadLists(), loadIsoJobs()]);
    setLoading(false);
  }, [loadKpis, loadProzesse, loadLists, loadIsoJobs]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Re-load Prozesse on filter change
  useEffect(() => { loadProzesse(); }, [filterStatus, filterAbteilung, loadProzesse]);

  // Poll active ISO job status
  useEffect(() => {
    if (!activeJob || activeJob.status === 'done' || activeJob.status === 'failed') return;
    const t = setInterval(async () => {
      try {
        const r = await api(`${ISO}/${activeJob.id}/status`);
        setActiveJob(r);
        if (r.status === 'done' || r.status === 'failed') {
          loadIsoJobs();
        }
      } catch (e) { /* noop */ }
    }, 1500);
    return () => clearInterval(t);
  }, [activeJob, loadIsoJobs]);

  const startIsoExport = async () => {
    setIsoStarting(true);
    try {
      const r = await fetch(ISO, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ von: isoVon, bis: isoBis }),
      });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        alert(e.error || 'Fehler beim Start');
        return;
      }
      const j = await r.json();
      setActiveJob(j);
      loadIsoJobs();
    } catch (e) {
      alert('Start fehlgeschlagen: ' + e.message);
    } finally {
      setIsoStarting(false);
    }
  };

  const downloadIsoPdf = (jobId) => {
    window.location.href = `${ISO}/${jobId}/pdf`;
  };

  const abteilungen = useMemo(() => {
    const map = new Map();
    for (const p of prozesse) {
      if (p.abteilung_id && p.abteilung_name) {
        map.set(p.abteilung_id, p.abteilung_name);
      }
    }
    return Array.from(map.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }, [prozesse]);

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
            <ShieldCheck size={24} />
            {t('qmPipeline.title', 'QM Pipeline-Dashboard')}
          </h1>
          <p className="text-sm mt-1" style={{ color: 'var(--text-tertiary)' }}>
            {t('qmPipeline.subtitle', 'Pipeline-Status aller Prozesse, Schulungen und Audit-Trail')}
          </p>
        </div>
        <button
          onClick={loadAll}
          disabled={loading}
          className="inline-flex items-center gap-2 px-3 py-1.5 border border-line rounded-lg text-sm hover:bg-[var(--bg-inset)]"
          style={{ color: 'var(--text-primary)' }}
        >
          {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
          {t('qmPipeline.reload', 'Neu laden')}
        </button>
      </div>

      {error && (
        <div className="px-4 py-2 bg-rose-50 border border-rose-200 rounded-lg text-sm text-rose-700">{error}</div>
      )}

      {/* KPI-Cards 6er Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <KpiCard
          label={t('qmPipeline.kpi.aktiv', 'Aktive Prozesse')}
          value={fmtInt(kpis?.prozesse_aktiv)}
          icon={Activity}
          onClick={() => { setFilterStatus('aktiv'); setDrill('aktive'); }}
        />
        <KpiCard
          label={t('qmPipeline.kpi.review', 'In Review')}
          value={fmtInt(kpis?.prozesse_in_review)}
          icon={Clock}
          accent={kpis?.prozesse_in_review > 0 ? '#F59E0B' : 'var(--text-primary)'}
          onClick={() => setDrill('review')}
        />
        <KpiCard
          label={t('qmPipeline.kpi.schulungen', 'Schulungen offen')}
          value={fmtInt(kpis?.schulungen_offen)}
          icon={BookOpen}
          accent={kpis?.schulungen_offen > 0 ? '#F59E0B' : 'var(--text-primary)'}
          onClick={() => setDrill('schulungen')}
        />
        <KpiCard
          label={t('qmPipeline.kpi.feedback', 'Feedback offen')}
          value={fmtInt(kpis?.feedback_offen)}
          icon={MessageCircle}
          accent={kpis?.feedback_offen > 0 ? '#338EDC' : 'var(--text-primary)'}
          onClick={() => setDrill('feedback')}
        />
        <KpiCard
          label={t('qmPipeline.kpi.compliance', 'Schulungs-Compliance')}
          value={fmtPct(kpis?.schulungs_compliance_pct)}
          sublabel={kpis ? `${kpis.compliance_compliant}/${kpis.compliance_gesamt}` : ''}
          icon={CheckCircle2}
          accent={kpis?.schulungs_compliance_pct >= 80 ? '#10B981' : kpis?.schulungs_compliance_pct >= 50 ? '#F59E0B' : '#EF4444'}
        />
        <KpiCard
          label={t('qmPipeline.kpi.ablauf', 'Zert. Ablauf < 30d')}
          value={fmtInt(kpis?.zertifikate_ablauf_30d)}
          icon={AlertTriangle}
          accent={kpis?.zertifikate_ablauf_30d > 0 ? '#EF4444' : 'var(--text-primary)'}
          onClick={() => setDrill('ablauf')}
        />
      </div>

      {/* Prozess-Liste mit Filter */}
      <div className="bg-surface-card border border-line rounded-card overflow-hidden">
        <div className="px-4 py-3 border-b border-line flex items-center justify-between flex-wrap gap-2">
          <h2 className="text-base font-semibold" style={{ color: 'var(--text-primary)' }}>
            {t('qmPipeline.prozesse.title', 'Prozesse')}
          </h2>
          <div className="flex items-center gap-2 flex-wrap">
            <Filter size={14} style={{ color: 'var(--text-tertiary)' }} />
            <select
              value={filterStatus}
              onChange={e => setFilterStatus(e.target.value)}
              className="text-sm border border-line rounded-lg px-2 py-1 bg-surface-card"
              style={{ color: 'var(--text-primary)' }}
            >
              <option value="">{t('qmPipeline.filter.allStatus', 'Alle Status')}</option>
              <option value="aktiv">{t('qmPipeline.status.aktiv', 'Aktiv')}</option>
              <option value="in_review">{t('qmPipeline.status.in_review', 'In Review')}</option>
              <option value="entwurf">{t('qmPipeline.status.entwurf', 'Entwurf')}</option>
              <option value="archiviert">{t('qmPipeline.status.archiviert', 'Archiviert')}</option>
            </select>
            <select
              value={filterAbteilung}
              onChange={e => setFilterAbteilung(e.target.value)}
              className="text-sm border border-line rounded-lg px-2 py-1 bg-surface-card"
              style={{ color: 'var(--text-primary)' }}
            >
              <option value="">{t('qmPipeline.filter.allAbteilungen', 'Alle Abteilungen')}</option>
              {abteilungen.map(([id, name]) => (
                <option key={id} value={id}>{name}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[var(--bg-inset,#F8F9FC)] text-[11px] uppercase tracking-wider"
                   style={{ color: 'var(--text-tertiary)' }}>
              <tr>
                <th className="text-left px-4 py-2">{t('qmPipeline.col.titel', 'Titel')}</th>
                <th className="text-left px-4 py-2">{t('qmPipeline.col.version', 'Version')}</th>
                <th className="text-left px-4 py-2">{t('qmPipeline.col.status', 'Status')}</th>
                <th className="text-left px-4 py-2">{t('qmPipeline.col.abteilung', 'Abteilung')}</th>
                <th className="text-left px-4 py-2">{t('qmPipeline.col.owner', 'Owner')}</th>
                <th className="text-right px-4 py-2">{t('qmPipeline.col.tests', 'Tests')}</th>
                <th className="text-right px-4 py-2">{t('qmPipeline.col.schulungen_offen', 'Sch. offen')}</th>
                <th className="text-left px-4 py-2">{t('qmPipeline.col.aktualisiert', 'Geändert')}</th>
                <th className="text-left px-4 py-2">{t('qmPipeline.col.letzter_major_bump', 'Letzter Major-Bump')}</th>
              </tr>
            </thead>
            <tbody>
              {prozesse.length === 0 && (
                <tr><td colSpan={9} className="px-4 py-8 text-center" style={{ color: 'var(--text-tertiary)' }}>
                  {t('qmPipeline.empty', 'Keine Prozesse')}
                </td></tr>
              )}
              {prozesse.map(p => (
                <tr key={p.id} className="border-t border-line odd:bg-[var(--bg-inset,#F8F9FC)]/30">
                  <td className="px-4 py-2">
                    <Link to={`/prozesse/${p.id}`} className="text-[#338edc] hover:underline">{p.titel}</Link>
                  </td>
                  <td className="px-4 py-2 font-mono text-[12px]" style={{ color: 'var(--text-secondary)' }}>{p.version_string}</td>
                  <td className="px-4 py-2"><StatusPill status={p.status} /></td>
                  <td className="px-4 py-2" style={{ color: 'var(--text-secondary)' }}>{p.abteilung_name || '—'}</td>
                  <td className="px-4 py-2" style={{ color: 'var(--text-secondary)' }}>{p.owner_name || '—'}</td>
                  <td className="px-4 py-2 text-right" style={{ color: 'var(--text-secondary)' }}>{p.tests_aktiv}</td>
                  <td className="px-4 py-2 text-right" style={{ color: p.schulungen_offen > 0 ? '#F59E0B' : 'var(--text-secondary)' }}>
                    {p.schulungen_offen}
                  </td>
                  <td className="px-4 py-2 text-[12px]" style={{ color: 'var(--text-tertiary)' }}>{fmtDate(p.aktualisiert_am)}</td>
                  <td className="px-4 py-2 text-[12px]" style={{ color: 'var(--text-tertiary)' }}>{fmtDate(p.letzter_major_bump)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* 4 Sektionen: Reviews / Schulungen / Feedback / Ablauf */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Fällige Reviews */}
        <div className="bg-surface-card border border-line rounded-card overflow-hidden">
          <div className="px-4 py-3 border-b border-line flex items-center justify-between">
            <h3 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
              {t('qmPipeline.section.reviews', 'Fällige Reviews')}
            </h3>
            <span className="text-[11px]" style={{ color: 'var(--text-tertiary)' }}>{reviewsList.length}</span>
          </div>
          <div className="max-h-72 overflow-y-auto">
            {reviewsList.length === 0 ? (
              <div className="p-4 text-sm text-center" style={{ color: 'var(--text-tertiary)' }}>
                {t('qmPipeline.empty.reviews', 'Keine offenen Reviews')}
              </div>
            ) : (
              <ul className="divide-y divide-line">
                {reviewsList.map(r => (
                  <li key={r.id} className="px-4 py-2 flex items-center justify-between text-sm">
                    <div>
                      <div style={{ color: 'var(--text-primary)' }}>{r.prozess_titel}</div>
                      <div className="text-[11px]" style={{ color: 'var(--text-tertiary)' }}>
                        v{r.version} • {fmtTageOffen(r.tage_offen)} {t('qmPipeline.offen', 'offen')}
                      </div>
                    </div>
                    <Link to={`/prozesse/${r.prozess_id}`} className="text-[#338edc] hover:underline text-[12px]">→</Link>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* Schulungs-Backlog */}
        <div className="bg-surface-card border border-line rounded-card overflow-hidden">
          <div className="px-4 py-3 border-b border-line flex items-center justify-between">
            <h3 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
              {t('qmPipeline.section.schulungen', 'Schulungs-Backlog')}
            </h3>
            <span className="text-[11px]" style={{ color: 'var(--text-tertiary)' }}>{schulungenList.length}</span>
          </div>
          <div className="max-h-72 overflow-y-auto">
            {schulungenList.length === 0 ? (
              <div className="p-4 text-sm text-center" style={{ color: 'var(--text-tertiary)' }}>
                {t('qmPipeline.empty.schulungen', 'Keine offenen Schulungen')}
              </div>
            ) : (
              <ul className="divide-y divide-line">
                {schulungenList.slice(0, 50).map(s => (
                  <li key={s.id} className="px-4 py-2 flex items-center justify-between text-sm">
                    <div>
                      <div style={{ color: 'var(--text-primary)' }}>{maName(s)}</div>
                      <div className="text-[11px]" style={{ color: 'var(--text-tertiary)' }}>
                        {s.test_titel} • {fmtTageOffen(s.tage_offen)} {t('qmPipeline.offen', 'offen')}
                        {s.ueberfaellig && <span className="ml-2 text-[#EF4444]">⚠ {t('qmPipeline.ueberfaellig', 'überfällig')}</span>}
                      </div>
                    </div>
                    <StatusPill status={s.status} />
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* Voice-Feedback-Backlog */}
        <div className="bg-surface-card border border-line rounded-card overflow-hidden">
          <div className="px-4 py-3 border-b border-line flex items-center justify-between">
            <h3 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
              {t('qmPipeline.section.feedback', 'Voice-Feedback-Backlog')}
            </h3>
            <span className="text-[11px]" style={{ color: 'var(--text-tertiary)' }}>{feedbackList.length}</span>
          </div>
          <div className="max-h-72 overflow-y-auto">
            {feedbackList.length === 0 ? (
              <div className="p-4 text-sm text-center" style={{ color: 'var(--text-tertiary)' }}>
                {t('qmPipeline.empty.feedback', 'Kein offenes Feedback')}
              </div>
            ) : (
              <ul className="divide-y divide-line">
                {feedbackList.slice(0, 50).map(f => (
                  <li key={f.id} className="px-4 py-2 flex items-center justify-between text-sm gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="truncate" style={{ color: 'var(--text-primary)' }}>{f.vorschlag_zusammenfassung || '—'}</div>
                      <div className="text-[11px]" style={{ color: 'var(--text-tertiary)' }}>
                        {f.prozess_titel || t('qmPipeline.feedback.kein_prozess', '(kein Prozess)')} • {fmtTageOffen(f.tage_offen)}
                      </div>
                    </div>
                    <PrioPill prio={f.prioritaet} />
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* Zertifikats-Ablauf */}
        <div className="bg-surface-card border border-line rounded-card overflow-hidden">
          <div className="px-4 py-3 border-b border-line flex items-center justify-between">
            <h3 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
              {t('qmPipeline.section.ablauf', 'Zertifikats-Ablauf < 30 Tage')}
            </h3>
            <span className="text-[11px]" style={{ color: 'var(--text-tertiary)' }}>{zertifikateList.length}</span>
          </div>
          <div className="max-h-72 overflow-y-auto">
            {zertifikateList.length === 0 ? (
              <div className="p-4 text-sm text-center" style={{ color: 'var(--text-tertiary)' }}>
                {t('qmPipeline.empty.ablauf', 'Keine ablaufenden Zertifikate')}
              </div>
            ) : (
              <ul className="divide-y divide-line">
                {zertifikateList.map(z => (
                  <li key={z.id} className="px-4 py-2 flex items-center justify-between text-sm">
                    <div>
                      <div style={{ color: 'var(--text-primary)' }}>{maName(z)}</div>
                      <div className="text-[11px]" style={{ color: 'var(--text-tertiary)' }}>
                        {z.test_titel} • {t('qmPipeline.gueltig_bis', 'bis')} {fmtDate(z.gueltig_bis)}
                      </div>
                    </div>
                    <span className={`text-[12px] font-semibold ${Number(z.tage_bis_ablauf) < 7 ? 'text-[#EF4444]' : 'text-[#F59E0B]'}`}>
                      {Math.max(0, Math.floor(Number(z.tage_bis_ablauf)))} d
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>

      {/* ISO-9001-Export */}
      <div className="bg-surface-card border border-line rounded-card overflow-hidden">
        <div className="px-4 py-3 border-b border-line">
          <h2 className="text-base font-semibold flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
            <FileText size={18} />
            {t('qmPipeline.iso.title', 'ISO-9001 Audit-Bericht')}
          </h2>
          <p className="text-[12px] mt-1" style={{ color: 'var(--text-tertiary)' }}>
            {t('qmPipeline.iso.subtitle', 'Generiert einen Audit-Bericht (PDF) mit Prozess-Verzeichnis, Schulungs-Nachweis, Feedback-Historie und Audit-Trail.')}
          </p>
        </div>
        <div className="p-4 space-y-4">
          <div className="flex items-end gap-3 flex-wrap">
            <div>
              <label className="block text-[11px] uppercase tracking-wider mb-1" style={{ color: 'var(--text-tertiary)' }}>
                {t('qmPipeline.iso.von', 'Von')}
              </label>
              <input type="date" value={isoVon} onChange={e => setIsoVon(e.target.value)}
                     className="border border-line rounded-lg px-3 py-1.5 text-sm bg-surface-card"
                     style={{ color: 'var(--text-primary)' }} />
            </div>
            <div>
              <label className="block text-[11px] uppercase tracking-wider mb-1" style={{ color: 'var(--text-tertiary)' }}>
                {t('qmPipeline.iso.bis', 'Bis')}
              </label>
              <input type="date" value={isoBis} onChange={e => setIsoBis(e.target.value)}
                     className="border border-line rounded-lg px-3 py-1.5 text-sm bg-surface-card"
                     style={{ color: 'var(--text-primary)' }} />
            </div>
            <button
              onClick={startIsoExport}
              disabled={isoStarting || (activeJob && activeJob.status !== 'done' && activeJob.status !== 'failed')}
              className="inline-flex items-center gap-2 px-4 py-1.5 bg-[#338edc] text-white rounded-lg text-sm font-semibold hover:bg-[#1A6BBF] disabled:opacity-50"
            >
              {isoStarting ? <Loader2 size={14} className="animate-spin" /> : <FileText size={14} />}
              {t('qmPipeline.iso.start', 'Bericht generieren')}
            </button>
          </div>

          {/* Active Job Status */}
          {activeJob && (
            <div className="border border-line rounded-lg p-3 bg-[var(--bg-inset,#F8F9FC)]">
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
                  {t('qmPipeline.iso.aktiverJob', 'Aktiver Job')} #{activeJob.id}
                </div>
                <StatusPill status={activeJob.status} />
              </div>
              <div className="w-full bg-line rounded-full h-2 overflow-hidden">
                <div className="bg-[#338edc] h-2 transition-all" style={{ width: `${activeJob.fortschritt_pct || 0}%` }}></div>
              </div>
              <div className="text-[11px] mt-1" style={{ color: 'var(--text-tertiary)' }}>
                {activeJob.fortschritt_pct || 0}%
                {activeJob.error_message && <span className="text-rose-600 ml-2">— {activeJob.error_message}</span>}
              </div>
              {activeJob.status === 'done' && (
                <button onClick={() => downloadIsoPdf(activeJob.id)}
                        className="mt-2 inline-flex items-center gap-2 px-3 py-1 bg-[#10B981] text-white rounded-lg text-sm">
                  <Download size={14} />
                  {t('qmPipeline.iso.download', 'PDF herunterladen')}
                </button>
              )}
            </div>
          )}

          {/* Job History */}
          {isoJobs.length > 0 && (
            <div>
              <h4 className="text-[12px] uppercase tracking-wider mb-2" style={{ color: 'var(--text-tertiary)' }}>
                {t('qmPipeline.iso.history', 'Verlauf')}
              </h4>
              <div className="border border-line rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-[var(--bg-inset,#F8F9FC)] text-[11px] uppercase tracking-wider"
                         style={{ color: 'var(--text-tertiary)' }}>
                    <tr>
                      <th className="text-left px-3 py-2">{t('qmPipeline.iso.col.gestartet', 'Gestartet')}</th>
                      <th className="text-left px-3 py-2">{t('qmPipeline.iso.col.zeitraum', 'Zeitraum')}</th>
                      <th className="text-left px-3 py-2">{t('qmPipeline.iso.col.status', 'Status')}</th>
                      <th className="text-right px-3 py-2">{t('qmPipeline.iso.col.prozesse', 'Proz.')}</th>
                      <th className="text-right px-3 py-2">{t('qmPipeline.iso.col.zertifikate', 'Zert.')}</th>
                      <th className="text-right px-3 py-2">{t('qmPipeline.iso.col.seiten', 'Seiten')}</th>
                      <th className="text-left px-3 py-2">{t('qmPipeline.iso.col.angefordert_von', 'Von')}</th>
                      <th className="text-right px-3 py-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {isoJobs.map(j => (
                      <tr key={j.id} className="border-t border-line">
                        <td className="px-3 py-2 text-[12px]" style={{ color: 'var(--text-secondary)' }}>{fmtDateTime(j.gestartet_am)}</td>
                        <td className="px-3 py-2 text-[12px]" style={{ color: 'var(--text-secondary)' }}>{j.zeitraum_von} → {j.zeitraum_bis}</td>
                        <td className="px-3 py-2"><StatusPill status={j.status} /></td>
                        <td className="px-3 py-2 text-right" style={{ color: 'var(--text-secondary)' }}>{j.anzahl_prozesse ?? '—'}</td>
                        <td className="px-3 py-2 text-right" style={{ color: 'var(--text-secondary)' }}>{j.anzahl_zertifikate ?? '—'}</td>
                        <td className="px-3 py-2 text-right" style={{ color: 'var(--text-secondary)' }}>{j.seitenzahl ?? '—'}</td>
                        <td className="px-3 py-2 text-[12px]" style={{ color: 'var(--text-tertiary)' }}>{j.gestartet_von_name || '—'}</td>
                        <td className="px-3 py-2 text-right">
                          {j.status === 'done' && (
                            <button onClick={() => downloadIsoPdf(j.id)}
                                    className="inline-flex items-center gap-1 text-[#338edc] hover:underline text-[12px]">
                              <Download size={12} /> PDF
                            </button>
                          )}
                          {j.status === 'failed' && j.error_message && (
                            <span className="text-rose-600 text-[11px]" title={j.error_message}>⚠</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Drill-Down Modals */}
      {drill === 'aktive' && (
        <DrillDownModal title={t('qmPipeline.drill.aktive', 'Aktive Prozesse')} onClose={() => setDrill(null)}>
          <ul className="divide-y divide-line text-sm">
            {prozesse.filter(p => p.status === 'aktiv').map(p => (
              <li key={p.id} className="py-2 flex items-center justify-between">
                <div>
                  <Link to={`/prozesse/${p.id}`} className="text-[#338edc] hover:underline">{p.titel}</Link>
                  <span className="ml-2 text-[11px]" style={{ color: 'var(--text-tertiary)' }}>v{p.version_string} • {p.abteilung_name}</span>
                </div>
                <ChevronRight size={14} style={{ color: 'var(--text-tertiary)' }} />
              </li>
            ))}
          </ul>
        </DrillDownModal>
      )}
      {drill === 'review' && (
        <DrillDownModal title={t('qmPipeline.drill.reviews', 'Prozesse in Review / fällige Reviews')} onClose={() => setDrill(null)}>
          <div className="space-y-4">
            <div>
              <h4 className="text-sm font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>{t('qmPipeline.drill.in_review', 'In Review')}</h4>
              <ul className="divide-y divide-line text-sm">
                {prozesse.filter(p => p.status === 'in_review').map(p => (
                  <li key={p.id} className="py-2"><Link to={`/prozesse/${p.id}`} className="text-[#338edc] hover:underline">{p.titel}</Link></li>
                ))}
                {prozesse.filter(p => p.status === 'in_review').length === 0 && <li className="py-2 text-[var(--text-tertiary)]">—</li>}
              </ul>
            </div>
            <div>
              <h4 className="text-sm font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>{t('qmPipeline.drill.review_antrag', 'Review-Anträge offen')}</h4>
              <ul className="divide-y divide-line text-sm">
                {reviewsList.map(r => (
                  <li key={r.id} className="py-2 flex justify-between">
                    <Link to={`/prozesse/${r.prozess_id}`} className="text-[#338edc] hover:underline">{r.prozess_titel} v{r.version}</Link>
                    <span className="text-[11px]" style={{ color: 'var(--text-tertiary)' }}>{fmtTageOffen(r.tage_offen)} offen</span>
                  </li>
                ))}
                {reviewsList.length === 0 && <li className="py-2 text-[var(--text-tertiary)]">—</li>}
              </ul>
            </div>
          </div>
        </DrillDownModal>
      )}
      {drill === 'schulungen' && (
        <DrillDownModal title={t('qmPipeline.drill.schulungen', 'Schulungs-Backlog (alle)')} onClose={() => setDrill(null)}>
          <table className="w-full text-sm">
            <thead className="bg-[var(--bg-inset,#F8F9FC)] text-[11px] uppercase">
              <tr>
                <th className="text-left px-2 py-1">{t('qmPipeline.col.ma', 'MA')}</th>
                <th className="text-left px-2 py-1">{t('qmPipeline.col.test', 'Test')}</th>
                <th className="text-left px-2 py-1">Status</th>
                <th className="text-right px-2 py-1">{t('qmPipeline.col.tage', 'Tage')}</th>
              </tr>
            </thead>
            <tbody>
              {schulungenList.map(s => (
                <tr key={s.id} className="border-t border-line">
                  <td className="px-2 py-1" style={{ color: 'var(--text-primary)' }}>{maName(s)}</td>
                  <td className="px-2 py-1" style={{ color: 'var(--text-secondary)' }}>{s.test_titel}</td>
                  <td className="px-2 py-1"><StatusPill status={s.status} /></td>
                  <td className="px-2 py-1 text-right" style={{ color: 'var(--text-tertiary)' }}>{fmtTageOffen(s.tage_offen)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </DrillDownModal>
      )}
      {drill === 'feedback' && (
        <DrillDownModal title={t('qmPipeline.drill.feedback', 'Voice-Feedback-Backlog (alle)')} onClose={() => setDrill(null)}>
          <ul className="divide-y divide-line text-sm">
            {feedbackList.map(f => (
              <li key={f.id} className="py-2 flex justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div style={{ color: 'var(--text-primary)' }}>{f.vorschlag_zusammenfassung}</div>
                  <div className="text-[11px]" style={{ color: 'var(--text-tertiary)' }}>
                    {f.prozess_titel || '(kein Prozess)'} • {f.ma_vorname || ''} {f.ma_nachname || ''}
                  </div>
                </div>
                <PrioPill prio={f.prioritaet} />
              </li>
            ))}
          </ul>
        </DrillDownModal>
      )}
      {drill === 'ablauf' && (
        <DrillDownModal title={t('qmPipeline.drill.ablauf', 'Zertifikats-Ablauf (30 Tage)')} onClose={() => setDrill(null)}>
          <table className="w-full text-sm">
            <thead className="bg-[var(--bg-inset,#F8F9FC)] text-[11px] uppercase">
              <tr>
                <th className="text-left px-2 py-1">MA</th>
                <th className="text-left px-2 py-1">Test</th>
                <th className="text-left px-2 py-1">{t('qmPipeline.col.gueltig_bis', 'Gültig bis')}</th>
                <th className="text-right px-2 py-1">{t('qmPipeline.col.tage', 'Tage')}</th>
              </tr>
            </thead>
            <tbody>
              {zertifikateList.map(z => (
                <tr key={z.id} className="border-t border-line">
                  <td className="px-2 py-1" style={{ color: 'var(--text-primary)' }}>{maName(z)}</td>
                  <td className="px-2 py-1" style={{ color: 'var(--text-secondary)' }}>{z.test_titel}</td>
                  <td className="px-2 py-1" style={{ color: 'var(--text-secondary)' }}>{fmtDate(z.gueltig_bis)}</td>
                  <td className="px-2 py-1 text-right" style={{ color: '#F59E0B' }}>{Math.max(0, Math.floor(Number(z.tage_bis_ablauf)))}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </DrillDownModal>
      )}
    </div>
  );
}
